/**
 * "Number settings" for WhatsApp numbers linked by QR -- the account owner's two per-number send limits:
 *   - Daily send cap per number (default 700)
 *   - Burst: sending pauses after this many messages within a minute (default 25)
 * Nothing else is shown or editable here. Whole numbers of 1 or more; an empty field means the default. Saved per
 * number; the server also checks the caller is the account owner. Rendered by Integrations.jsx (an owner-only page).
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton, Typography, TextField, Stack, Alert, CircularProgress, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { fetchWorkspaces } from "../../../redux/Slice/Workspace/workspaceSlice";
import { useWhatsAppQrNumbers } from "../../../features/whatsapp/hooks/useWhatsAppQrNumbers";

const asField = (saved) => (saved === null || saved === undefined ? "" : String(saved));

const NumberSettingsRow = ({ item, defaults, onSave }) => {
  const [daily, setDaily] = useState(asField(item.settings?.dailyMessageLimit));
  const [burst, setBurst] = useState(asField(item.settings?.burstLimit));
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Pick up a saved value that arrives later (the list refreshes in the background).
  useEffect(() => {
    setDaily(asField(item.settings?.dailyMessageLimit));
    setBurst(asField(item.settings?.burstLimit));
  }, [item.settings?.dailyMessageLimit, item.settings?.burstLimit]);

  const isWhole = (v) => v === "" || /^[1-9]\d*$/.test(v.trim());
  const dirty = daily.trim() !== asField(item.settings?.dailyMessageLimit) || burst.trim() !== asField(item.settings?.burstLimit);

  const save = async () => {
    if (!isWhole(daily) || !isWhole(burst)) {
      setLocalError("Enter a whole number of 1 or more, or leave a field empty to use the default.");
      return;
    }
    setLocalError(null);
    setBusy(true);
    try {
      await onSave(item.connectionId, {
        dailyMessageLimit: daily.trim() === "" ? null : Number(daily),
        burstLimit: burst.trim() === "" ? null : Number(burst),
      });
      toast.success(`Saved settings for ${item.label || "this number"}.`);
    } catch (_) {
      // the hook already surfaced the server's message in the dialog's banner
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ py: 1.5 }}>
      <Typography className="qr-name" sx={{ fontWeight: 600, fontSize: "1.3rem" }}>{item.label || item.phoneNumber || `Number ${item.slot + 1}`}</Typography>
      {item.phoneNumber && <Typography variant="body1" color="text.secondary">+{String(item.phoneNumber).replace(/^\+/, "")}</Typography>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1.5 }}>
        <TextField
          fullWidth
          label="Daily send cap"
          placeholder={String(defaults.dailyMessageLimit)}
          value={daily}
          onChange={(e) => setDaily(e.target.value.replace(/[^\d]/g, ""))}
          inputProps={{ inputMode: "numeric" }}
          helperText={`Messages per day for this number (default ${defaults.dailyMessageLimit})`}
        />
        <TextField
          fullWidth
          label="Burst"
          placeholder={String(defaults.burstLimit)}
          value={burst}
          onChange={(e) => setBurst(e.target.value.replace(/[^\d]/g, ""))}
          inputProps={{ inputMode: "numeric" }}
          helperText={`Pause after this many sends in a minute (default ${defaults.burstLimit})`}
        />
      </Stack>
      {localError && <Typography variant="body2" color="error" sx={{ display: "block", mt: 0.5 }}>{localError}</Typography>}
      <Button size="large" variant="contained" disabled={busy || !dirty} onClick={save} sx={{ textTransform: "none", mt: 1.5 }}>
        {busy ? "Saving…" : "Save"}
      </Button>
    </Box>
  );
};

const WhatsAppQrNumberSettingsDialog = ({ open, onClose, workspaceId }) => {
  const dispatch = useDispatch();
  const { connections, defaults, loading, error, saveSettings } = useWhatsAppQrNumbers(workspaceId, open);
  // A new daily cap / burst limit changes what the chats' composers show (the "daily limit reached" / "resting" banner is
  // read from the workspace data). Refresh that data right after a successful save so the banners follow the new limits
  // without a page reload -- e.g. a raised cap clears a banner at once, a lowered one below today's count shows it.
  const handleSave = async (id, settings) => {
    const out = await saveSettings(id, settings);
    dispatch(fetchWorkspaces());
    return out;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { bgcolor: "background.paper", borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "1.5rem", fontWeight: 700, px: 4, pt: 3 }}>
        Number settings
        <IconButton onClick={onClose} size="medium">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4, "& .MuiTypography-root:not(.qr-name)": { fontSize: "1.1rem", lineHeight: 1.6 }, "& .MuiButton-root": { fontSize: "1.1rem", px: 2.5, py: 1 }, "& .MuiInputBase-input": { fontSize: "1.05rem", py: 1.6 }, "& .MuiInputLabel-root": { fontSize: "1.05rem" }, "& .MuiFormHelperText-root": { fontSize: "0.9rem" }, "& .MuiChip-label": { fontSize: "0.95rem", px: 1.5 } }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>
        ) : connections.length === 0 ? (
          <Typography variant="body1" color="text.secondary">Link a WhatsApp number first, then its settings appear here.</Typography>
        ) : (
          connections.map((c, i) => (
            <React.Fragment key={c.connectionId}>
              {i > 0 && <Divider />}
              <NumberSettingsRow item={c} defaults={defaults} onSave={handleSave} />
            </React.Fragment>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3 }}>
        <Button size="large" onClick={onClose} sx={{ textTransform: "none", fontSize: "1.05rem" }}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppQrNumberSettingsDialog;
